import { useState } from "react";

export type LocalPagination = {
  totalRecords: number;
  totalPages: number;
  perPage: number;
  currentPage: number;
  prevPage: boolean;
  nextPage: boolean;
  series: (number | string)[];
};

export type UseLocalPaginationReturnType = {
  setPerPage: (perPage: number) => void;
  setCurrentPage: (page: number) => void;
  pagination: LocalPagination;
};

/**
 *  we have to show 5 pages at a time.
 * "..." - space, number - clickable, "number" - not clickable;
 *  expected outputs when we have 30 pages:
 * - ["1", 2, 3, 4, "...", 30] if current page is 1
 * - [1, "2", 3, 4, "...", 30] if current page is 2
 * - [1, 2, "3", 4, "...", 30] if current page is 3
 * - [1, "...", 14, "15", 16, "...", 30] if current page is 15
 * - [1, 2 "...", "28", 29, 30] if current page is 28
 * - [1, 2, "...", 28, "29", 30] if current page is 29
 *  */
export const getSeries = (totalPages: number, currentPage: number): (number | string)[] => {
  const series: (number | string)[] = [];
  series.push(currentPage === 1 ? "1" : 1);
  if (totalPages <= 7) {
    for (let i = 2; i <= totalPages; i++) {
      series.push(i === currentPage ? `${i}` : i);
    }
    return series;
  }
  if (currentPage <= 4) {
    for (let i = 2; i <= 5; i++) {
      series.push(i === currentPage ? `${i}` : i);
    }
    series.push("...");
    series.push(totalPages);
  } else if (currentPage >= totalPages - 3) {
    series.push("...");
    for (let i = totalPages - 4; i < totalPages; i++) {
      series.push(i === currentPage ? `${i}` : i);
    }
    series.push(totalPages === currentPage ? `${totalPages}` : totalPages);
  } else {
    series.push("...");
    series.push(currentPage - 1);
    series.push(`${currentPage}`);
    series.push(currentPage + 1);
    series.push("...");
    series.push(totalPages);
  }

  return series;
};

export const useLocalPagination = <T>({
  data,
}: {
  data: T[];
}): UseLocalPaginationReturnType & { currentData: T[] } => {
  const [curr, setCurr] = useState<LocalPagination["currentPage"]>(1);
  const [per, setPer] = useState<LocalPagination["perPage"]>(10);

  return {
    setPerPage: (val: number) => {
      setPer(val);
      setCurr(1);
    },
    setCurrentPage: (val: number) => setCurr(val),
    currentData: data ? data.slice((curr - 1) * per, curr * per) : [],
    pagination: {
      perPage: per,
      totalRecords: data.length,
      totalPages: Math.ceil(data.length / per),
      currentPage: curr,
      prevPage: curr > 1,
      nextPage: curr < Math.ceil(data.length / per),
      series: getSeries(Math.ceil(data.length / per), curr),
    },
  };
};

export default useLocalPagination;
